import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Button, Flex, Text, Title } from '@inflearn/ds-react-next';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';

import { includes } from 'lodash-es';
import { ApiError } from '../../../../../../resources/ApiError';
import { logger } from '../../../../../../resources/Logger';
import { useReferUrlNavigation } from '../../../../../hooks/useReferUrlNavigation';
import useClientAuthChangePasswordVerificationEmailCreateMutation from '../../../../../mutations/useClientAuthChangePasswordVerificationEmailCreateMutation';
import EmailValidator from '../../../../../validators/EmailValidator';
import { ERROR_MESSAGE } from '../../../../../validators/constants';
import EmailSentModal from '../../../../common/EmailSentModal';
import { EmailFormItem, type EmailFormValue } from './EmailFormItem';

const FIND_PASSWORD_ERROR_CODE = {
  ACCOUNT_NOT_FOUND: 'ACCOUNT_NOT_FOUND',
  ACCOUNT_NOT_VERIFIED: 'ACCOUNT_NOT_VERIFIED',
  EMAIL_SEND_FAILED: 'EMAIL_SEND_FAILED',
} as const;

const FIND_PASSWORD_ERROR_MESSAGE: Record<keyof typeof FIND_PASSWORD_ERROR_CODE, () => string> = {
  [FIND_PASSWORD_ERROR_CODE.ACCOUNT_NOT_FOUND]: ERROR_MESSAGE.EMAIL_NOT_EXIST,
  [FIND_PASSWORD_ERROR_CODE.ACCOUNT_NOT_VERIFIED]: ERROR_MESSAGE.ACCOUNT_NOT_VERIFIED,
  [FIND_PASSWORD_ERROR_CODE.EMAIL_SEND_FAILED]: ERROR_MESSAGE.TRY_AGAIN,
};

const hasValidErrorCode = (
  errorCode?: string,
): errorCode is keyof typeof FIND_PASSWORD_ERROR_CODE => {
  return includes(Object.values(FIND_PASSWORD_ERROR_CODE), errorCode);
};

export const FindPasswordPageItem = () => {
  const { t } = useTranslation();
  const [isOpen, { open }] = useDisclosure(false);

  const { getReferUrl } = useReferUrlNavigation();
  const {
    register,
    formState: { errors },
    getValues,
    handleSubmit,
    setError,
  } = useForm<EmailFormValue>({
    mode: 'all',
    resolver: classValidatorResolver(EmailValidator),
  });
  const { mutate, isLoading } = useClientAuthChangePasswordVerificationEmailCreateMutation({
    onError: (error) => {
      if (ApiError.isApiError(error)) {
        if (!hasValidErrorCode(error.errorCode)) {
          setError('email', {
            type: 'manual',
            message: ERROR_MESSAGE.WRONG_EMAIL_ADDRESS(),
          });
          return;
        }

        const getErrorMessage = FIND_PASSWORD_ERROR_MESSAGE[error.errorCode];
        setError('email', {
          type: 'manual',
          message: getErrorMessage(),
        });
        return;
      }

      setError('email', {
        type: 'manual',
        message: ERROR_MESSAGE.WRONG_EMAIL_ADDRESS(),
      });

      logger.error(error);
    },
    onSuccess: () => {
      open();
    },
  });

  const handleFormSubmit = () => {
    mutate({
      email: getValues('email'),
      referUrl: getReferUrl(),
    });
  };

  return (
    <>
      <Flex direction="column" justify="center" align="center" mb={24} gap={4}>
        <Title order={2}>{t('비밀번호 찾기')}</Title>
        <Text ta="center">
          {t('가입한 이메일을 입력해 주세요.')}

          <br />
          {t('이메일을 통해 비밀번호 변경 링크가 전송됩니다')}
        </Text>
      </Flex>
      <Flex
        component="form"
        direction="column"
        gap={16}
        w="100%"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <EmailFormItem register={register} errors={errors} showLabel={false} />
        <Button
          type="submit"
          variant="filled"
          color="infgreen"
          size="md"
          fullWidth
          loading={isLoading}
        >
          {t('변경 링크 전송하기')}
        </Button>
      </Flex>
      <EmailSentModal
        title={t('비밀번호 설정 메일 발송')}
        opened={isOpen}
        email={getValues('email')}
        descriptions={[
          t('위 이메일로 비밀번호 설정 메일이 발송되었습니다.'),
          t('메일이 확인되지 않을 경우, 스팸함을 확인해 주세요.'),
        ]}
      />
    </>
  );
};
